import React from 'react';
import CoachingPlan from 'records/CoachingPlan';
import steps from './steps';
import MeetingType from 'records/MeetingType';
import CoachingContentDetail, {defaultSkillGoal} from 'records/CoachingContentDetail';
import {List, Set} from 'immutable';
import CompleteContainer from './steps/CompleteContainer';
import Wizard from 'components/wizard/Wizard';
import moment from 'moment';
import CoachingReviewTypeStore from 'stores/coaching/CoachingReviewTypeStore';
import connectToStores from 'alt-utils/lib/connectToStores';
import CoachingReviewTypeActions from 'actions/coaching/CoachingReviewTypeActions';
import AuthStore from 'stores/AuthStore';
import CoachingParticipant from 'records/CoachingParticipant';

@connectToStores
export default class CoachingPlanWizard extends React.PureComponent {
  constructor(props) {
    super(props);

    const now = moment();
    const oneHourAgo = now.subtract(1, 'hours');

    // props.plan is for edit - came from kotlin, so "unbox" it here
    // preloadedMiniWizardPlan is to preserve what came from the mini wizard
    if (this.props.plan) {
      this.state = {model: this.props.plan.fromApi()};
    } else if (this.props.preloadedMiniWizardPlan) {
      const toPreload = this.props.preloadedMiniWizardPlan
        .set(
          'contentDetails',
          this.props.preloadedMiniWizardPlan.contentDetails.isEmpty()
            ? List([contentDetail(this.props.skillName)])
            : this.props.preloadedMiniWizardPlan.contentDetails
        )
        .set(
          'recipient',
          !this.props.preloadedMiniWizardPlan.recipient
            ? new CoachingParticipant({
                id: undefined,
                info: undefined,
                prepNumberOfCalls: 1,
                prepShouldComment: true,
              })
            : this.props.preloadedMiniWizardPlan.recipient
        )
        .set(
          'coach',
          !this.props.preloadedMiniWizardPlan.coach
            ? new CoachingParticipant({
                prepNumberOfCalls: 1,
              })
            : this.props.preloadedMiniWizardPlan.coach
        )
        .set('reviewTypeIds', Set(this.props.preloadedMiniWizardPlan.reviewTypeIds));

      this.state = {model: toPreload};
    } else {
      this.state = {
        model: new CoachingPlan({
          defaultMeetingType: new MeetingType({
            meetingType: 'PhysicalLocation',
          }),
          contentDetails: List([contentDetail(this.props.skillName)]),
          recipient: {
            id: props.preloadRep && props.preloadRep.id,
            info: props.preloadRep && props.preloadRep.toJS(),
            prepNumberOfCalls: 1,
            prepShouldComment: true,
          },
          coach: {
            prepNumberOfCalls: 1,
          },
          schedule: {
            weeksBetween: 1,
            daysOfWeek: [now.format('dddd')],
            time: {
              hour: oneHourAgo.format('HH'),
              minute: 0,
            },
            recurrenceCount: 8,
            durationInMinutes: 15,
          },
          reviewTypeIds: Set(),
          useGenericQuestions: false,
        }),
      };
    }

    this.getSteps = this.getSteps.bind(this);
  }

  static getStores() {
    return [CoachingReviewTypeStore, AuthStore];
  }

  static getPropsFromStores() {
    return {
      types: CoachingReviewTypeStore.getState().types,
      sessionUser: AuthStore.getState().user,
    };
  }

  componentDidMount() {
    CoachingReviewTypeActions.fetchAllReviewTypes();
  }

  getSteps() {
    // XXX
    // Beware that when defining `shouldDisplay` the result of that call
    // should remain relatively static through the usage of the wizard.
    // If it needs to change, it should happen early, before the user has
    // taken any actions.
    //
    // If a step's index changes after the user has already visited it,
    // it will cause problems with our `completedSteps` Set and possibly
    // allow the form to be submitted prematurely, or silently skip
    // a step of the wizard.

    return steps.filter(step => (step.shouldDisplay ? step.shouldDisplay(this.props) : true));
  }

  render() {
    const {onClose, editMode} = this.props;
    const editProps = {
      title: 'Edit Coaching Plan',
      frozenSteps: List([0, 1, 5, 7]),
      startStep: 2,
      submit: this.props.editCoachingPlan,
    };
    const defaultProps = {
      title: 'Create New Coaching Plan',
      frozenSteps: List(),
      startStep: 0,
      submit: this.props.createCoachingPlan,
    };

    return (
      <Wizard
        {...(editMode ? editProps : defaultProps)}
        steps={steps}
        complete={CompleteContainer}
        model={this.state.model}
        onClose={onClose}
        getSteps={this.getSteps}
        {...this.props}
      />
    );
  }
}

/**
 * Create a new content detail.
 * @param skillName name of the skill. If falsy, returns a non-skill custom content detail instead.
 * @returns {CoachingContentDetail}
 */
function contentDetail(skillName) {
  return skillName
    ? new CoachingContentDetail({
        isSkillBased: true,
        skillGoal: defaultSkillGoal,
        skillName: this.props.skillName,
      })
    : new CoachingContentDetail({
        isSkillBased: false,
      });
}
